/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartGroupTotals } from '@common/types';
import { COMMON_TRANSLATION } from '@/common/constants';
import { DiseasesIds } from './DiseaseCard/DiseaseCard.types';
import { DiseasesSectionProps } from './DiseasesSection.types';
import { ErrorData } from '@/common/components';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import {
  StyledCancerIcon,
  StyledColonIcon,
  StyledDiseasesCardsContainer,
  StyledHeartIcon,
  StyledResultsDeliveredContainer,
  StyledFootContent,
} from './DiseasesSection.styles';

import DiseaseCard from './DiseaseCard/DiseaseCard';
import DiseasesChartCard from './DiseasesChartCard/DiseasesChartCard';
import TotalResultsCard from './TotalResultsCard/TotalResultsCard';

export const DiseasesSection: FC<DiseasesSectionProps> = ({ data, isError }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [diseasesChartData, setDiseasesChartData] = useState<ChartGroupTotals[]>([
    {
      name: t('diseases.ls'),
      total: 0,
      percentage: 0,
    },
    {
      name: t('diseases.fh'),
      total: 0,
      percentage: 0,
    },
    {
      name: t('diseases.hboc'),
      total: 0,
      percentage: 0,
    },
  ]);

  const resultsDelivered = data.delivered;
  const lsResults = data.ls;
  const fhResults = data.fh;
  const hbocResults = data.hboc;

  useEffect(() => {
    setDiseasesChartData([
      {
        name: t('diseases.ls'),
        total: lsResults.total,
        percentage: lsResults.percentage,
      },
      {
        name: t('diseases.fh'),
        total: fhResults.total,
        percentage: fhResults.percentage,
      },
      {
        name: t('diseases.hboc'),
        total: hbocResults.total,
        percentage: hbocResults.percentage,
      },
    ]);
  }, [data]);

  return isError && !data ? (
    <ErrorData css={{ height: '500px' }} />
  ) : (
    <>
      <StyledResultsDeliveredContainer>
        <TotalResultsCard
          isDataAvailable={!isError}
          totalResults={resultsDelivered.total}
          totalNegative={resultsDelivered.negative}
          totalPositive={resultsDelivered.positive}
          positivePercentage={resultsDelivered.positivePercentage}
          negativePercentage={resultsDelivered.negativePercentage}
        />
        <DiseasesChartCard
          data={diseasesChartData}
          isDataAvailable={!isError}
          noDataAvailableMessage={t('no_data_available', { ns: COMMON_TRANSLATION })}
        />
      </StyledResultsDeliveredContainer>
      <StyledDiseasesCardsContainer>
        <DiseaseCard
          icon={<StyledColonIcon />}
          isDataAvailable={!isError}
          resultsNumber={lsResults.total}
          resultsPercentage={lsResults.totalPercentage}
          title={t('diseases.ls')}
          testId={DiseasesIds.LS}
        />
        <DiseaseCard
          icon={<StyledHeartIcon />}
          isDataAvailable={!isError}
          resultsNumber={fhResults.total}
          resultsPercentage={fhResults.totalPercentage}
          title={t('diseases.fh')}
          testId={DiseasesIds.FH}
        />
        <DiseaseCard
          icon={<StyledCancerIcon />}
          isDataAvailable={!isError}
          resultsNumber={hbocResults.total}
          resultsPercentage={hbocResults.totalPercentage}
          title={t('diseases.hboc')}
          testId={DiseasesIds.HBOC}
        />
      </StyledDiseasesCardsContainer>
      <StyledFootContent>
        <p>{t('cdc_tier_1_foot_note')}</p>
      </StyledFootContent>
    </>
  );
};
